import { useCallback, useEffect, useState } from "react";
import Navbar from "@/components/Navbar";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { ALT } from "@/constants/testIds";
import { useAuth } from "@/context/AuthContext";
import MemberCTA from "@/components/MemberCTA";

const fmt = (c) => `$${(c / 100).toFixed(2)}`;

export default function AltFinancier() {
  const { user } = useAuth();
  const [dash, setDash] = useState(null);
  const [requests, setRequests] = useState([]);
  const [posts, setPosts] = useState([]);
  const [postId, setPostId] = useState("");
  const [amount, setAmount] = useState("1.00");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const [{ data: d }, { data: reqs }, { data: ps }] = await Promise.all([
      api.get("/economics/dashboard"),
      api.get("/alt-financier/requests"),
      api.get("/posts", { params: { limit: 50 } }),
    ]);
    setDash(d);
    setRequests(reqs);
    setPosts(ps.filter((p) => !p.financing_bounced));
    setPostId((current) => current || (ps.length > 0 ? ps[0].post_id : ""));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/alt-financier/requests", {
        post_id: postId,
        amount_cents: Math.round(parseFloat(amount) * 100),
        reason,
      });
      toast.success("Request approved · funds reserved");
      setReason("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Request failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0b0c10] telesis-grain">
      <Navbar />
      <main data-testid={ALT.widget} className="max-w-5xl mx-auto px-4 md:px-8 py-10 grid lg:grid-cols-5 gap-6">
        <div className="lg:col-span-3 space-y-4">
          <p className="telesis-eyebrow">Alternate Financier · 60% pool</p>
          <h1 className="text-3xl md:text-4xl font-black text-slate-100 tracking-tight">
            Company funds that can finance any approved mentation
          </h1>
          <p className="text-sm text-slate-400 leading-relaxed">
            First-come, first-served. 2% of the pool is the maximum per single request this month.
          </p>
          {dash && (
            <div className="telesis-panel rounded p-5 space-y-2 text-sm" data-testid={ALT.poolTotal}>
              <p>Pool: <b className="text-emerald-300">{fmt(dash.alt_financier_pool_cents)}</b></p>
              <p>Spent: <b>{fmt(dash.alt_pool_spent_cents)}</b></p>
              <p>Available: <b className="text-yellow-300">{fmt(dash.alt_pool_available_cents)}</b></p>
              <p data-testid={ALT.capBadge}>Max per request (2%): <b>{fmt(dash.alt_per_request_cap_cents)}</b></p>
            </div>
          )}

          {user?.is_member ? (
            <form data-testid={ALT.form} onSubmit={submit} className="telesis-panel rounded p-5 space-y-4">
              <div>
                <label className="text-xs uppercase tracking-wider text-slate-400">Post to finance</label>
                <select
                  value={postId}
                  onChange={(e) => setPostId(e.target.value)}
                  required
                  className="mt-1 w-full bg-[#12141c] border border-[#222634] text-slate-100 rounded px-3 py-2 text-sm"
                >
                  {posts.map((p) => (
                    <option key={p.post_id} value={p.post_id}>
                      {p.author_name} — {p.content.slice(0, 50)}…
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-slate-400">Amount (CAD)</label>
                <Input
                  data-testid={ALT.amount}
                  type="number"
                  min="1"
                  step="0.5"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  className="mt-1 bg-[#12141c] border-[#222634] text-slate-100 font-mono"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-slate-400">Reason</label>
                <Textarea
                  data-testid={ALT.reason}
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  required
                  minLength={5}
                  rows={3}
                  className="mt-1 bg-[#12141c] border-[#222634] text-slate-100 resize-none"
                />
              </div>
              <Button
                data-testid={ALT.submit}
                disabled={busy}
                type="submit"
                className="w-full rounded-full bg-emerald-500 hover:bg-emerald-400 text-black font-semibold"
              >
                {busy ? "…" : "Apply · First-come, first-served"}
              </Button>
            </form>
          ) : (
            <MemberCTA label="Membership required to apply for alternate financing" />
          )}
        </div>

        <div className="lg:col-span-2 space-y-3">
          <p className="telesis-eyebrow">Request queue · this month</p>
          {requests.length === 0 && <p className="text-xs text-slate-500 font-mono">no requests yet</p>}
          {requests.map((r) => (
            <div key={r.request_id} className="telesis-panel rounded p-4">
              <p className="text-xs text-slate-500 font-mono uppercase tracking-widest">{r.status}</p>
              <p className="text-lg font-bold text-emerald-300 mt-1">{fmt(r.amount_cents)}</p>
              <p className="text-xs text-slate-300 mt-1">{r.user_name} · {r.reason}</p>
            </div>
          ))}
        </div>
      </main>
    </div>
  );
}
